<template>
  <div class="date-filter text-center">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      offset-y
      right
      @update:return-value="enabledConfirmButton = false"
    >
      <template v-slot:activator="{ on, attrs }">
        <DsIcon
          name="dates"
          size="small"
          space="left"
          class="date-filter__dates-icon"
        />

        <DsIcon
          v-bind="attrs"
          name="menu-down"
          size="small"
          space="right"
          class="date-filter__menu-icon date-filter__menu-icon--down"
        />

        <DsIcon
          v-bind="attrs"
          name="menu-up"
          size="small"
          space="right"
          class="date-filter__menu-icon date-filter__menu-icon--up"
        />

        <DsTextField
          id="option-1"
          v-model="dataLabel"
          :readonly="true"
          input-class="dense"
          class="date-filter__text-field"
          name="option-2"
          label="Intervalo de tempo *"
          v-bind="attrs"
          v-on="on"
        />
      </template>

      <v-card v-if="isMobile()">
        <vc-date-picker
          v-model="range"
          :color="datePickerColorTheme"
          :is-dark="$vuetify.theme.dark"
          :max-date="maxDate"
          locale="pt-BR"
          is-range
          @input="saveAction"
        />
      </v-card>

      <v-card v-else>
        <v-row>
          <v-row>
            <vc-date-picker
              v-model="range"
              :color="datePickerColorTheme"
              class="d-sm-flex pt-3 ml-6"
              :columns="2"
              :is-dark="$vuetify.theme.dark"
              :max-date="maxDate"
              locale="pt-BR"
              mode="dateTime"
              :update-on-input="true"
              is24hr
              is-range
              @dayfocusout="enabledButton()"
              @input="enabledButton()"
            />
          </v-row>

          <div class="mr-3 ml-4">
            <v-list dense>
              <v-list-item-group
                v-model="selectedItem"
                color="primary"
              >
                <v-list-item
                  v-for="(period, index) in relativePeriodsList"
                  :key="index"
                  @click="moveCalendar(period)"
                >
                  <v-list-item-content>
                    <v-list-item-title
                      v-text="period.label"
                    />
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>
        </v-row>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            outlined
            rounded
            @click="cancelAction"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            rounded
            :disabled="!enabledConfirmButton"
            @click="saveAction"
          >
            {{ primaryBtnLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import filtersModel from '@/mixins/filters-model';
import moment from 'moment';
import {
  NORMALIZED_PATTERN,
  NORMALIZED_FULL_PATTERN,
  PERIOD_LABEL,
} from '@/utils/date';
import { DsTextField } from '@ds/components';
import { DsIcon } from '@ds/icons';
import { mapMutations, mapState } from 'vuex';
import { MUTATE } from '@/store/modules/mutation_types';

export default {
  name: 'DateFilter',
  components: {
    DsTextField,
    DsIcon,
  },
  mixins: [
    filtersModel,
  ],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    primaryBtnLabel: {
      type: String,
      default: 'Filtrar',
    },
  },
  data() {
    return {
      menu: false,
      selectedItem: null,
      enabledConfirmButton: false,
      temporaryRange: {
        start: null,
        end: null,
      },
      relativePeriodsList: [
        {
          label: PERIOD_LABEL.TODAY.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().startOf('date');
            this.temporaryRange.end = moment().endOf('date');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.TODAY.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.YESTERDAY.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().subtract('1', 'day').startOf('date');
            this.temporaryRange.end = moment().subtract('1', 'day').endOf('date');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.YESTERDAY.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.LAST_24_HOURS.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().subtract('24', 'hour');
            this.temporaryRange.end = moment();

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.LAST_24_HOURS.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.CURRENT_WEEK.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().startOf('week');
            this.temporaryRange.end = moment().endOf('date');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.CURRENT_WEEK.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.CURRENT_MOUTH.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().startOf('month').startOf('date');
            this.temporaryRange.end = moment().endOf('date');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.CURRENT_MOUTH.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.LAST_MOUTH.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().subtract('1', 'month').startOf('month').startOf('date');
            this.temporaryRange.end = moment().subtract('1', 'month').endOf('month');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.LAST_MOUTH.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.LAST_7_DAYS.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().subtract('6', 'day').startOf('date');
            this.temporaryRange.end = moment().endOf('date');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.LAST_7_DAYS.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
        {
          label: PERIOD_LABEL.LAST_30_DAYS.LABEL,
          moveToSpecificPeriod() {
            this.temporaryRange.start = moment().subtract('29', 'day').startOf('date');
            this.temporaryRange.end = moment().endOf('date');

            this.mutateMisc({
              property: 'previousDataLabel',
              with: PERIOD_LABEL.LAST_30_DAYS.PREVIOUS_PERIOD_LABEL,
            });
          },
        },
      ],
    };
  },
  computed: {
    ...mapState('misc', [
      'previousDataLabel',
    ]),
    maxDate() {
      return moment().endOf('day')._d;
    },
    dataLabel() {
      return `${this.temporaryRange.start.format('L')} - ${this.temporaryRange.end.format('L')}`;
    },
    range: {
      get() {
        return {
          start: this.temporaryRange.start.format(),
          end: this.temporaryRange.end.format(),
        };
      },
      set(newDate) {
        this.temporaryRange.start = moment(newDate.start);
        this.temporaryRange.end = moment(newDate.end);
        this.temporaryRange.period = newDate.period || '';
      },
    },
    datePickerColorTheme() {
      return this.$vuetify.theme.dark ? 'teal' : 'purple';
    },
  },
  watch: {
    value(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.temporaryRange = newValue;
    },
  },
  created() {
    this.temporaryRange = {
      start: this.value.start,
      end: this.value.end,
      period: this.value.period,
    };

    this.mutateMisc({
      property: 'previousDataLabel',
      with: PERIOD_LABEL.CUSTOM.PREVIOUS_PERIOD_LABEL,
    });
  },
  methods: {
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    moveCalendar(period) {
      period.moveToSpecificPeriod.bind(this)();

      this.temporaryRange = {
        start: moment(this.range.start, NORMALIZED_PATTERN),
        end: moment(this.range.end, NORMALIZED_PATTERN),
        period: period.label,
      };

      this.menu = false;
      this.$emit('input', this.temporaryRange);
    },
    cancelAction() {
      this.temporaryRange.start = this.value.start;
      this.temporaryRange.end = this.value.end;

      this.temporaryRange = {
        start: moment(this.value.start, NORMALIZED_PATTERN),
        end: moment(this.value.end, NORMALIZED_PATTERN),
        period: this.value.period || 'Custom',
      };

      this.menu = false;
    },
    saveAction() {
      const startDate = this.temporaryRange.start._i || this.temporaryRange.start._d;
      const endDate = this.temporaryRange.end._i || this.temporaryRange.end._d;

      this.temporaryRange = {
        start: moment(startDate, NORMALIZED_FULL_PATTERN),
        end: moment(endDate, NORMALIZED_FULL_PATTERN),
        period: 'Custom',
      };

      this.mutateMisc({
        property: 'previousDataLabel',
        with: PERIOD_LABEL.CUSTOM.PREVIOUS_PERIOD_LABEL,
      });

      this.$emit('input', this.temporaryRange);

      this.selectedItem = null;
      this.menu = false;
    },
    enabledButton() {
      this.enabledConfirmButton = true;
    },
  },
};
</script>

<style scoped lang="scss">
  .date-filter {
    position: relative;
    display: flex;
    margin-top: 2px;

    &__dates-icon {
      position: absolute;
      left: 13px;
      top: 16px;
      pointer-events: none;
      z-index: inherit;
    }

    &__menu-icon {
      position: absolute;
      right: 5px;
      top: 16px;
      pointer-events: none;
      z-index: inherit;
      display: none;

      &--up[aria-expanded="true"] {
        display: block;
      }

      &--down[aria-expanded="false"] {
        display: block;
      }
    }
  }

  .date-picker {
    z-index: 9;
    width: 240px;
    background: transparent;
    cursor: pointer;
  }

  .theme--dark .date-picker {
    color: white;
  }

  .vc-grid-container {
    z-index: 99;
  }

  ::v-deep {
    .vc-pane-container div:nth-child(3) {
      display: flex;
      justify-content: space-around;
    }

    .vc-time-picker.vc-bordered {
      width: 100%;
      justify-content: center;
      border-color: transparent;
    }

    .vc-container.vc-is-dark {
      border-color: transparent;
      background-color: transparent;
    }

    .v-text-field__slot {
      min-width: 190px;
    }

    .v-btn {
      text-transform: capitalize;

      &__content {
        font-weight: 600;
      }
    }

    .ds-textfield input#option-1 {
      font-size: var(--ds-font-size-xs);
      padding: 12px 24px 10px 36px;
      line-height: 12px;
    }

    .ds-textfield > input + span {
      font-weight: var(--ds-font-weight-semibold);
    }

    .ds-icon--size-small {
      font-size: 10px;
    }

    .v-list-item--active {
      background-color: rgba(var(--ds-color-interactive), 0.16);
      color: var(--ds-color-interactive);
    }
  }
</style>
